import React from "react"
import {
  Box,
  Button,
  HStack,
  Icon,
  Image,
  Input,
  InputElement,
  InputGroup,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import PropertyCard from "./PropertyCard"
import { SelectFilter } from "../SelectFilter"
import StateBox from "../common/StateBox"
import type { ViewMode } from "../common/ViewModeToggle"
import PropertyMapView, {
  toMapCoordinate,
  type PropertyMapItem,
} from "../maps/PropertyMapView"
import { getHomeOpportunityDetailLine } from "../sourcing/HomeOpportunityCard"
import { useActivePortfolio } from "../../hooks/useActivePortfolio"
import { useDebounce } from "../../hooks/useDebounce"
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll"
import {
  useDeleteHome,
  useInfiniteHomes,
  usePortfolioStats
} from "../../hooks/usePortfolioQueries"
import { toaster } from "../../toaster"
import type { Home } from "../../services/portfolioApi"

const isBatchImportEnabled =
  process.env.REACT_APP_ENABLE_BATCH_IMPORT === "true"

// ─── Portfolio summary cards ──────────────────────────────────────────────────

const potentialConfig = [
  { key: "veryHigh", label: "Very High" },
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" }
] as const

const cardStyle = {
  bg: "gray.100",
  p: 5,
  borderRadius: "lg",
  flex: 1
} as const

const PortfolioSummaryCards: React.FC<{ portfolioId?: number }> = ({
  portfolioId
}) => {
  const { data: stats } = usePortfolioStats(portfolioId)

  const totalHomes = stats?.totalHomes ?? 0
  const unanalysed = stats?.unanalysedHomes ?? 0
  const counts = stats?.zeroBillPotentialCounts ?? {
    veryHigh: 0,
    high: 0,
    medium: 0,
    low: 0
  }
  const avgRoi = stats?.avgRoi ?? null
  const avgPayback = stats?.avgPayback ?? null
  const avgUpgradesCost = stats?.avgUpgradeCost ?? null

  return (
    <HStack gap={3} align="stretch" w="full">
      <Box {...cardStyle}>
        <Text fontSize="sm" color="gray.600">
          Total number of homes
        </Text>
        <Text fontSize="3xl" fontWeight="bold" color="gray.900">
          {totalHomes}
        </Text>
        {unanalysed > 0 ? (
          <Text fontSize="xs" color="gray.500">
            {unanalysed} could not be analysed
          </Text>
        ) : (
          <Text fontSize="xs" color="gray.500">
            All homes analysed
          </Text>
        )}
      </Box>

      <Box {...cardStyle} flex={2}>
        <Text fontSize="sm" color="gray.600">
          Zero Bill Potential
        </Text>
        <HStack gap={8} mt={1}>
          {potentialConfig.map(({ key, label }) => (
            <VStack key={key} align="center" gap={0}>
              <Text fontSize="3xl" fontWeight="bold" color="gray.900">
                {counts[key as keyof typeof counts] ?? 0}
              </Text>
              <Text fontSize="xs" color="gray.500">
                {label}
              </Text>
            </VStack>
          ))}
        </HStack>
      </Box>

      <Box {...cardStyle}>
        <Text fontSize="sm" color="gray.600">
          Avg. retrofit ROI
        </Text>
        <Text fontSize="3xl" fontWeight="bold" color="gray.900">
          {avgRoi != null ? `${avgRoi.toFixed(1)}%` : "–"}
        </Text>
        <Text fontSize="xs" color="gray.500">
          {avgPayback != null
            ? `~${avgPayback.toFixed(0)} yr payback`
            : "No data yet"}
        </Text>
      </Box>

      <Box {...cardStyle}>
        <Text fontSize="sm" color="gray.600">
          Avg. upgrade cost
        </Text>
        <Text fontSize="3xl" fontWeight="bold" color="gray.900">
          {avgUpgradesCost != null
            ? `£${Math.round(avgUpgradesCost).toLocaleString()}`
            : "–"}
        </Text>
        <Text fontSize="xs" color="gray.500">
          Solar · Battery · Heat pump
        </Text>
      </Box>
    </HStack>
  )
}

// ─── Portfolio content (shared between Portfolio page and My Opportunities tab) ─

interface PortfolioContentProps {
  heading?: React.ReactNode
  headerActions?: React.ReactNode
  showStats?: boolean
  renderCard?: (home: Home) => React.ReactNode
  hideControls?: boolean
  externalDebouncedSearch?: string
  externalBuiltForm?: string
  externalSort?: "recent" | "alphabetical" | "oldest" | "highest_roi"
  viewMode?: ViewMode
  mapStorageKey?: string
  mapDetailFrom?: string
}

const PortfolioContent: React.FC<PortfolioContentProps> = ({
  heading,
  headerActions,
  showStats = true,
  renderCard,
  hideControls = false,
  externalDebouncedSearch,
  externalBuiltForm,
  externalSort,
  viewMode = "list",
  mapStorageKey,
  mapDetailFrom,
}) => {
  const navigate = useNavigate()
  const { activePortfolioId, isQuotaReached } = useActivePortfolio()

  const [builtFormFilter, setBuiltFormFilter] = useState("")
  const [sortOption, setSortOption] = useState<
    "recent" | "alphabetical" | "oldest" | "highest_roi"
  >("recent")
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearch = useDebounce(searchQuery, 800)

  useEffect(() => {
    setBuiltFormFilter("")
    setSearchQuery("")
  }, [activePortfolioId])

  const effectiveSearch = hideControls ? (externalDebouncedSearch ?? "") : debouncedSearch
  const effectiveBuiltForm = hideControls ? (externalBuiltForm ?? "") : builtFormFilter
  const effectiveSort = hideControls ? (externalSort ?? "recent") : sortOption

  const {
    data: homesPages,
    isLoading: homesLoading,
    error: homesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteHomes(
    20,
    activePortfolioId,
    effectiveSearch || undefined,
    effectiveBuiltForm || undefined,
    effectiveSort
  )

  const homes = useMemo(
    () => homesPages?.pages.flatMap((page) => page.homes) ?? [],
    [homesPages]
  )

  const mapItems = useMemo<PropertyMapItem[]>(
    () =>
      homes.map((home) => ({
        id: `home-${home.id}`,
        homeId: home.id,
        address: home.fullAddress,
        lat: toMapCoordinate(home.lat),
        lng: toMapCoordinate(home.lng),
        imageUrl: home.imageUrl ?? null,
        detailLine: getHomeOpportunityDetailLine(home),
        roi: home.upgradesRoi,
        upgradeCost: home.upgradesTotalCost,
        paybackPeriod: home.upgradesPaybackPeriod,
      })),
    [homes]
  )

  const loadMoreRef = useInfiniteScroll(fetchNextPage, hasNextPage, isFetchingNextPage)

  const { data: statsData } = usePortfolioStats(activePortfolioId)
  const deleteHomeMutation = useDeleteHome()

  const handleDeleteProperty = async (homeId: number, address: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${address} from your portfolio?`
    )
    if (confirmed) {
      try {
        await deleteHomeMutation.mutateAsync(homeId)
      } catch {
        toaster.create({
          title: "Error",
          description: "There was an error deleting your home. Please try again.",
          type: "error",
          duration: 4000,
        })
      }
    }
  }

  return (
    <VStack gap={6} align="stretch">
      {heading && (
        <HStack justify="space-between" align="center" w="full">
          {heading}
          <HStack gap={2} align="center">
            {isBatchImportEnabled && (
              <Button
                variant="outline"
                onClick={() =>
                  navigate("/bulk-import", {
                    state: { portfolioId: activePortfolioId }
                  })
                }
                size="md"
              >
                Import batch
              </Button>
            )}
            <Button
              bg={isQuotaReached ? "gray.400" : "green.800"}
              color="white"
              _hover={{ bg: isQuotaReached ? "gray.400" : "green.700" }}
              onClick={() =>
                !isQuotaReached &&
                navigate("/add-property", {
                  state: { portfolioId: activePortfolioId }
                })
              }
              size="md"
              disabled={isQuotaReached}
            >
              {isQuotaReached ? "Quota Reached" : "Add a home"}
            </Button>
          </HStack>
        </HStack>
      )}

      {homesLoading ? (
        <StateBox>
          <Spinner size="lg" />
          <Text mt={4}>Loading your portfolio...</Text>
        </StateBox>
      ) : homesError ? (
        <StateBox>
          <Text color="red.500">
            Error loading portfolio. Please try again.
          </Text>
        </StateBox>
      ) : (
        <>
          {homes.length === 0 && !effectiveSearch && !effectiveBuiltForm ? (
            <StateBox>
              <VStack gap={6}>
                <Image
                  src="/images/placeholer-add-homes.png"
                  alt="Add homes"
                  width={240}
                  height={240}
                />
                <Text fontSize="lg" color="gray.500">
                  To get started, add all the addresses you'd like Greenflip
                  to analyse.
                </Text>
                <HStack gap={3} justify="center" flexWrap="wrap">
                  <Button
                    bg={isQuotaReached ? "gray.400" : "green.800"}
                    color="white"
                    size="lg"
                    px={8}
                    _hover={{ bg: isQuotaReached ? "gray.400" : "green.700" }}
                    disabled={isQuotaReached}
                    onClick={() =>
                      !isQuotaReached &&
                      navigate("/add-property", {
                        state: { portfolioId: activePortfolioId }
                      })
                    }
                  >
                    {isQuotaReached ? "Quota Reached" : "Add a home"}
                  </Button>
                  {isBatchImportEnabled && (
                    <Button
                      variant="outline"
                      size="lg"
                      px={8}
                      onClick={() =>
                        navigate("/bulk-import", {
                          state: { portfolioId: activePortfolioId }
                        })
                      }
                    >
                      Import batch
                    </Button>
                  )}
                </HStack>
              </VStack>
            </StateBox>
          ) : (
            <>
              {headerActions && (() => {
                const total = homesPages?.pages[0]?.totalHomes ?? 0
                return (
                  <HStack justify="space-between" align="center">
                    {total > 0 ? (
                      <Text fontWeight="semibold" fontSize="md" color="gray.700">
                        {total} {total === 1 ? "property" : "properties"}
                      </Text>
                    ) : <Box />}
                    <HStack gap={2}>{headerActions}</HStack>
                  </HStack>
                )
              })()}
              {showStats && (
                <PortfolioSummaryCards portfolioId={activePortfolioId} />
              )}
              {!hideControls && (
                <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
                  <HStack gap={3} flex={1}>
                    <InputGroup
                      maxW="320px"
                      startElement={
                        <InputElement pointerEvents="none">
                          <Icon color="gray.400" boxSize={4}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="11" cy="11" r="8" />
                              <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                          </Icon>
                        </InputElement>
                      }
                    >
                      <Input
                        placeholder="Search address..."
                        value={searchQuery}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setSearchQuery(e.target.value)
                        }
                        borderRadius="8px"
                      />
                    </InputGroup>
                    <SelectFilter value={builtFormFilter} onChange={setBuiltFormFilter} placeholder>
                      <option value="">All built forms</option>
                      {(statsData?.availableBuiltForms ?? []).map((form) => (
                        <option key={form} value={form}>
                          {form}
                        </option>
                      ))}
                    </SelectFilter>
                  </HStack>
                  <SelectFilter
                    value={sortOption}
                    onChange={(v) => setSortOption(v as typeof sortOption)}
                  >
                    <option value="alphabetical">Alphabetical</option>
                    <option value="recent">Most recent</option>
                    <option value="oldest">Oldest first</option>
                    <option value="highest_roi">Highest ROI</option>
                  </SelectFilter>
                </HStack>
              )}
              {homes.length === 0 ? (
                <StateBox>
                  <Text color="gray.600">
                    No homes match the current filters.
                  </Text>
                </StateBox>
              ) : viewMode === "map" ? (
                <PropertyMapView
                  items={mapItems}
                  storageKey={mapStorageKey ?? `portfolio.mapViewport.${activePortfolioId ?? "default"}`}
                  onViewDetails={(item) => {
                    const options = mapDetailFrom ? { state: { from: mapDetailFrom } } : undefined
                    navigate(`/home/${item.homeId}`, options)
                  }}
                  hasMore={hasNextPage}
                  isLoadingMore={isFetchingNextPage}
                  onLoadMore={() => fetchNextPage()}
                />
              ) : (
                <VStack gap={4} align="stretch">
                  {homes.map((home) => (
                    renderCard ? (
                      <React.Fragment key={home.id}>{renderCard(home)}</React.Fragment>
                    ) : (
                      <PropertyCard
                        key={home.id}
                        home={home}
                        onClick={(id) => navigate(`/home/${id}`)}
                      />
                    )
                  ))}
                  {hasNextPage && (
                    <Box ref={loadMoreRef} py={4} textAlign="center">
                      <HStack justify="center" gap={2}>
                        <Spinner size="sm" color="gray.400" />
                        <Text color="gray.500">Loading more...</Text>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              )}
            </>
          )}
        </>
      )}
    </VStack>
  )
}

export default PortfolioContent
